import { useEffect, useMemo, useState } from 'react';

import { PostCallAnalysisMetric } from '../Services/PostCallAnalysisApiProviderTypes';
import { SelectOption } from '../types';
import { CustomSelect } from './CustomFormElements';
import Button from './Buttons';

interface ExportParams {
  start_date?: string;
  end_date?: string;
  chat_fields: string[];
}

interface PostCallAnalyticsExportBoxProps {
  metrics: PostCallAnalysisMetric[];
  startDate?: string;
  endDate?: string;
  onExport: (params: ExportParams) => Promise<void>;
}

const PostCallAnalyticsExportBox = ({
  metrics = [],
  startDate,
  endDate,
  onExport,
}: PostCallAnalyticsExportBoxProps) => {
  const sortedMetrics = useMemo(
    () => [...metrics].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [metrics],
  );

  const fieldOptions = useMemo<SelectOption[]>(
    () => sortedMetrics.map((metric) => ({ value: metric.name, label: metric.display_name })),
    [sortedMetrics],
  );

  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const metricNamesKey = useMemo(() => sortedMetrics.map((m) => m.name).join('|'), [sortedMetrics]);
  useEffect(() => {
    setSelectedFields(sortedMetrics.map((metric) => metric.name));
  }, [metricNamesKey]);

  const [exporting, setExporting] = useState<boolean>(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await onExport({
        start_date: startDate,
        end_date: endDate,
        chat_fields: selectedFields,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="border-yes radius-yes p-2 w-100">
      <h3 className="h3-title">Export to XLS</h3>
      <div className="small-font mb-2">
        Choose which chat fields to include as columns in the <b>chats</b> tab of the exported Excel
        file.
      </div>

      {sortedMetrics.length > 0 && (
        <div className="mb-2" style={{ maxWidth: '500px' }}>
          <CustomSelect
            id="export-chat-fields"
            options={fieldOptions}
            value={selectedFields}
            multiSelect
            placeholder="Chat fields"
            onChange={(value) => setSelectedFields(Array.isArray(value) ? value : [])}
          />
        </div>
      )}

      <Button
        variant="primary"
        size="medium"
        onClick={handleExport}
        disabled={!startDate || exporting}
      >
        <i className="fa-solid fa-file-excel"></i> {exporting ? 'Exporting…' : 'Export'}
      </Button>
    </div>
  );
};

export default PostCallAnalyticsExportBox;
